/**
 * Registering the service worker (`sw.ts`) and handling its updates safely:
 * a new build installs and WAITS; the page is told (`onUpdateReady`) and the
 * operator decides when to reload — the waiting worker takes over (SKIP_WAITING)
 * and the page reloads once it controls the page. A first install, which has
 * nothing to replace, just takes the page over silently.
 *
 * The browser's `navigator.serviceWorker` is a port, so the flow is
 * unit-tested with fakes.
 */

/**
 * The message a waiting worker takes over on. Restated here rather than
 * imported from `policy.ts`: a module both the page and the worker imported
 * would become a shared chunk, and `sw.js` must stay one classic script with
 * no imports. `sw.test.ts` holds the two equal.
 */
export const SKIP_WAITING_MESSAGE = 'acp.skip-waiting';

export type WorkerLike = {
  readonly state: string;
  postMessage(message: unknown): void;
  addEventListener(type: 'statechange', listener: () => void): void;
};

export type RegistrationLike = {
  readonly waiting: WorkerLike | null;
  readonly installing: WorkerLike | null;
  addEventListener(type: 'updatefound', listener: () => void): void;
  update(): Promise<unknown>;
};

export type ContainerLike = {
  /** The worker controlling this page; null on a first visit (or a hard reload). */
  readonly controller: unknown;
  register(url: string, options: { scope: string; updateViaCache: 'none' }): Promise<RegistrationLike>;
  addEventListener(type: 'controllerchange', listener: () => void): void;
};

export type RegisterDeps = {
  readonly container: ContainerLike;
  /** A new build is installed and waiting: show a prompt; `apply()` switches to it and reloads. */
  readonly onUpdateReady: (apply: () => void) => void;
  readonly reload: () => void;
  /** Check for a new build every so often, so a long-open operator tab learns of a deploy. */
  readonly every: (ms: number, fn: () => void) => void;
  readonly reportError: (error: unknown) => void;
};

/** How often an open tab asks for a new build. */
export const UPDATE_CHECK_MS = 30 * 60 * 1000;

export async function registerServiceWorker(deps: RegisterDeps): Promise<void> {
  const { container } = deps;
  let applied = false;
  const offer = (worker: WorkerLike): void => {
    deps.onUpdateReady(() => {
      applied = true;
      worker.postMessage({ type: SKIP_WAITING_MESSAGE });
    });
  };
  // Reload only for an update the operator asked for — not when a first install claims the page.
  container.addEventListener('controllerchange', () => {
    if (applied) deps.reload();
  });
  let registration: RegistrationLike;
  try {
    registration = await container.register('/sw.js', { scope: '/', updateViaCache: 'none' });
  } catch (err) {
    deps.reportError(err);
    return;
  }
  // An update that finished installing before this page loaded.
  if (registration.waiting !== null && container.controller !== null) offer(registration.waiting);
  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    if (installing === null) return;
    installing.addEventListener('statechange', () => {
      // Installed while another worker controls the page: an update, waiting for the operator.
      if (installing.state === 'installed' && container.controller !== null) offer(installing);
    });
  });
  deps.every(UPDATE_CHECK_MS, () => {
    registration.update().catch(deps.reportError);
  });
}
